import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  Filter,
  Layers,
  LocateFixed,
  Ruler,
} from "lucide-react";
import {
  CLOSE_MAP_TOOLS_PANELS_EVENT,
  MAP_TOOLS_PANEL_STATE_EVENT,
  SUPPRESS_NEXT_MAP_TOOLS_TOGGLE_EVENT,
} from "./mapToolsEvents";

export default function MapToolsMenu({
  onOpenFilters,
  onOpenLayers,
  onToggleMeasure,
  onLocateMe,
  isLocationPanelOpen = false,
  isCatchMarkerDetailsOpen = false,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMeasureActive, setIsMeasureActive] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const rootRef = useRef(null);
  const suppressNextToggleRef = useRef(false);

  const shouldHideForCatchMarker =
    isMobileViewport && isCatchMarkerDetailsOpen;

  useEffect(() => {
    if (isLocationPanelOpen || shouldHideForCatchMarker) {
      setIsOpen(false);
    }
  }, [isLocationPanelOpen, shouldHideForCatchMarker]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleCloseMapToolsPanels = () => {
      setIsOpen(false);
    };

    window.addEventListener(
      CLOSE_MAP_TOOLS_PANELS_EVENT,
      handleCloseMapToolsPanels
    );
    return () => {
      window.removeEventListener(
        CLOSE_MAP_TOOLS_PANELS_EVENT,
        handleCloseMapToolsPanels
      );
    };
  }, []);

  useEffect(() => {
    const handleSuppressNextToggle = () => {
      suppressNextToggleRef.current = true;

      window.setTimeout(() => {
        suppressNextToggleRef.current = false;
      }, 250);
    };

    window.addEventListener(
      SUPPRESS_NEXT_MAP_TOOLS_TOGGLE_EVENT,
      handleSuppressNextToggle
    );
    return () => {
      window.removeEventListener(
        SUPPRESS_NEXT_MAP_TOOLS_TOGGLE_EVENT,
        handleSuppressNextToggle
      );
    };
  }, []);

  useEffect(() => {
    const handleMapToolPanelState = (event) => {
      if (event?.detail?.id !== "measure") {
        return;
      }

      const nextIsMeasureActive = Boolean(event.detail.isOpen);
      setIsMeasureActive(nextIsMeasureActive);

      if (nextIsMeasureActive) {
        setIsOpen(false);
      }
    };

    window.addEventListener(MAP_TOOLS_PANEL_STATE_EVENT, handleMapToolPanelState);
    return () => {
      window.removeEventListener(
        MAP_TOOLS_PANEL_STATE_EVENT,
        handleMapToolPanelState
      );
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
        detail: { id: "tools-menu", isOpen },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
          detail: { id: "tools-menu", isOpen: false },
        })
      );
    };
  }, [isOpen]);

  const runTool = (handler) => {
    setIsOpen(false);

    const runAfterMenuCloses = () => {
      handler?.();
    };

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(runAfterMenuCloses);
      return;
    }

    setTimeout(runAfterMenuCloses, 0);
  };

  const tools = [
    {
      key: "filters",
      label: t("map_filter_button", { defaultValue: "Filters" }),
      icon: Filter,
      onClick: () => runTool(onOpenFilters),
      showChevron: true,
    },
    {
      key: "layers",
      label: t("map_tools_layers", { defaultValue: "Map Layers" }),
      icon: Layers,
      onClick: () => runTool(onOpenLayers),
      showChevron: true,
    },
    {
      key: "measure",
      label: t("map_measure_distance", { defaultValue: "Measure Distance" }),
      icon: Ruler,
      onClick: () => runTool(onToggleMeasure),
      showChevron: true,
    },
    {
      key: "my-location",
      label: t("map_tools_my_location", { defaultValue: "My Location" }),
      icon: LocateFixed,
      onClick: () => runTool(onLocateMe),
      showChevron: true,
    },
  ];

  if (isLocationPanelOpen || shouldHideForCatchMarker) {
    return null;
  }

  return (
    <div
      className={`map-tools${isMeasureActive ? " is-measure-active" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`map-tools-pill${isOpen ? " is-open" : ""}`}
        aria-label={t("map_tools_button", { defaultValue: "Map Tools" })}
        aria-expanded={isOpen}
        onClick={() => {
          if (suppressNextToggleRef.current) {
            suppressNextToggleRef.current = false;
            return;
          }

          setIsOpen((current) => !current);
        }}
      >
        <img
          src="/banner/maptoolsbutton.png"
          alt=""
          aria-hidden="true"
          className="map-tools-pill-image"
        />
        <span className="map-tools-pill-text">
          {t("map_tools_button", { defaultValue: "Map Tools" })}
        </span>
      </button>

      {isOpen && (
        <div
          className="map-tools-sheet"
          role="menu"
          aria-label={t("map_tools_button", { defaultValue: "Map Tools" })}
        >
          <span className="map-tools-sheet__handle" aria-hidden="true" />
          <div className="map-tools-grid">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.key}
                  type="button"
                  className="map-tools-card"
                  role="menuitem"
                  onClick={tool.onClick}
                >
                  <span className="map-tools-card__icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={2.1} />
                  </span>
                  <span className="map-tools-card__label">{tool.label}</span>
                  {tool.showChevron && (
                    <ChevronRight
                      className="map-tools-card__chevron"
                      size={17}
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
